import Phaser from '../../lib/phaser.js';
import { Character } from './character.js';
import { DIRECTION } from '../../common/direction.js';
import { exhaustiveGuard } from '../../utils/guard.js';
import { SKIN_IDLE_FRAME_CONFIG, resolveSkinKey, skinForNpcId } from './skins.js';

// NPC overworld skin sheets are 32px; scale x2 to read at ~one tile (TILE_SIZE 64).
const SKIN_SCALE = 2;

/**
 * @typedef {keyof typeof NPC_MOVEMENT_PATTERN} NpcMovementPattern
 */

/** @enum {NpcMovementPattern} */
export const NPC_MOVEMENT_PATTERN = Object.freeze({
  IDLE: 'IDLE',
  CLOCKWISE: 'CLOCKWISE',
  SET_PATH: 'SET_PATH',
});

/**
 * @typedef NPCPath
 * @type {Object.<number, import('../../types/typedef.js').Coordinate>}
 */

/**
 * @typedef NPCConfigProps
 * @type {object}
 * @property {number} frame
 * @property {NPCPath} npcPath
 * @property {NpcMovementPattern} movementPattern
 * @property {import('../../types/typedef.js').NpcEvent[]} events
 * @property {string} animationKeyPrefix
 * @property {number} id
 * @property {string} [skinKey] overworld skin asset key; defaults to a per-id skin
 */

/**
 * @typedef {Omit<import('./character').CharacterConfig, 'assetKey' | 'idleFrameConfig'> & NPCConfigProps} NPCConfig
 */

export class NPC extends Character {
  /** @type {boolean} */
  #talkingToPlayer;
  /** @type {NPCPath} */
  #npcPath;
  /** @type {number} */
  #currentPathIndex;
  /** @type {NpcMovementPattern} */
  #movementPattern;
  /** @type {number} */
  #lastMovementTime;
  /** @type {import('../../types/typedef.js').NpcEvent[]} */
  #events;
  /** @type {string} */
  #skinKey;
  /** @type {number} */
  #id;

  /**
   * @param {NPCConfig} config
   */
  constructor(config) {
    // NPCs now use the MPWSP overworld skins (same as the player). Each NPC id maps to a
    // distinct skin so the world is populated by varied characters, not one reused sheet.
    const skinKey = resolveSkinKey(config.skinKey ?? skinForNpcId(config.id));
    super({
      ...config,
      assetKey: skinKey,
      scale: SKIN_SCALE,
      origin: { x: 0, y: 0 },
      idleFrameConfig: { ...SKIN_IDLE_FRAME_CONFIG },
    });

    this.#talkingToPlayer = false;
    this.#npcPath = config.npcPath;
    this.#currentPathIndex = 0;
    this.#movementPattern = config.movementPattern;
    this.#lastMovementTime = Phaser.Math.Between(3500, 5000);
    this.#events = config.events;
    this.#skinKey = skinKey;
    this.#id = config.id;
  }

  /** @type {import('../../types/typedef.js').NpcEvent[]} */
  get events() {
    return [...this.#events];
  }

  /** @type {boolean} */
  get isTalkingToPlayer() {
    return this.#talkingToPlayer;
  }

  /**
   * @param {boolean} val
   */
  set isTalkingToPlayer(val) {
    this.#talkingToPlayer = val;
  }

  /** @type {number} */
  get id() {
    return this.#id;
  }

  /** @type {NPCPath} */
  get npcPath() {
    return this.#npcPath;
  }

  /**
   * @param {NPCPath} val
   */
  set npcPath(val) {
    this.#npcPath = val;
  }

  /**
   * @param {NpcMovementPattern} val
   */
  set npcMovementPattern(val) {
    this.#movementPattern = val;
  }

  /**
   * @param {() => void | undefined} val
   */
  set finishedMovementCallback(val) {
    this._spriteGridMovementFinishedCallback = val;
  }

  /**
   * Resets the lastMovementTime, which is used for when we want to have an npc start moving
   * immediately. This is needed for cutscene support so after the npc appears, that npc starts
   * moving directly to the player.
   * @returns {void}
   */
  resetMovementTime() {
    this.#lastMovementTime = 0;
  }

  /**
   * @param {import('../../common/direction.js').Direction} playerDirection
   * @returns {void}
   */
  facePlayer(playerDirection) {
    // Turn to face the player: the NPC looks in the OPPOSITE direction the player is facing.
    this._phaserGameObject.anims.stop();
    this._phaserGameObject.setFlipX(false);
    switch (playerDirection) {
      case DIRECTION.DOWN:
        this._phaserGameObject.setFrame(this._idleFrameConfig.UP);
        break;
      case DIRECTION.LEFT:
        this._phaserGameObject.setFrame(this._idleFrameConfig.RIGHT);
        break;
      case DIRECTION.RIGHT:
        this._phaserGameObject.setFrame(this._idleFrameConfig.LEFT);
        break;
      case DIRECTION.UP:
        this._phaserGameObject.setFrame(this._idleFrameConfig.DOWN);
        break;
      case DIRECTION.NONE:
        break;
      default:
        exhaustiveGuard(playerDirection);
    }
  }

  /**
   * @param {DOMHighResTimeStamp} time
   * @returns {void}
   */
  update(time) {
    if (this._isMoving) {
      return;
    }
    if (this.#talkingToPlayer) {
      return;
    }
    super.update(time);

    if (this.#movementPattern === NPC_MOVEMENT_PATTERN.IDLE) {
      return;
    }

    if (this.#lastMovementTime < time) {
      /** @type {import('../../common/direction.js').Direction} */
      let characterDirection = DIRECTION.NONE;
      let nextPosition = this.#npcPath[this.#currentPathIndex + 1];

      // validate if we actually moved to the next position, if not, skip updating index
      const prevPosition = this.#npcPath[this.#currentPathIndex];
      // guard against a movement pattern with a missing/empty path — treat as idle instead of crashing every frame
      if (prevPosition === undefined) {
        return;
      }
      if (prevPosition.x !== this._phaserGameObject.x || prevPosition.y !== this._phaserGameObject.y) {
        nextPosition = this.#npcPath[this.#currentPathIndex];
      } else {
        if (nextPosition === undefined) {
          // if npc is following a set path, once we reach the end, stop moving the npc
          if (this.#movementPattern === NPC_MOVEMENT_PATTERN.SET_PATH) {
            this.#movementPattern = NPC_MOVEMENT_PATTERN.IDLE;
            this.#currentPathIndex = 0;
            return;
          }
          nextPosition = this.#npcPath[0];
          this.#currentPathIndex = 0;
        } else {
          this.#currentPathIndex = this.#currentPathIndex + 1;
        }
      }

      if (nextPosition.x > this._phaserGameObject.x) {
        characterDirection = DIRECTION.RIGHT;
      } else if (nextPosition.x < this._phaserGameObject.x) {
        characterDirection = DIRECTION.LEFT;
      } else if (nextPosition.y < this._phaserGameObject.y) {
        characterDirection = DIRECTION.UP;
      } else if (nextPosition.y > this._phaserGameObject.y) {
        characterDirection = DIRECTION.DOWN;
      }

      this.moveCharacter(characterDirection);
      if (this.#movementPattern === NPC_MOVEMENT_PATTERN.SET_PATH) {
        this.#lastMovementTime = time;
      } else {
        this.#lastMovementTime = time + Phaser.Math.Between(2000, 5000);
      }
    }
  }

  /**
   * @param {import('../../common/direction.js').Direction} direction
   * @returns {void}
   */
  moveCharacter(direction) {
    super.moveCharacter(direction);

    switch (this._direction) {
      case DIRECTION.DOWN:
      case DIRECTION.LEFT:
      case DIRECTION.RIGHT:
      case DIRECTION.UP:
        if (
          !this._phaserGameObject.anims.isPlaying ||
          this._phaserGameObject.anims.currentAnim?.key !== `SKIN_${this.#skinKey}_${this._direction}`
        ) {
          this._phaserGameObject.setFlipX(false);
          this._phaserGameObject.play(`SKIN_${this.#skinKey}_${this._direction}`);
        }
        break;
      case DIRECTION.NONE:
        break;
      default:
        // We should never reach this default case
        exhaustiveGuard(this._direction);
    }
  }
}
